import {Suspense} from 'react'
import {ErrorBoundary} from '@sentry/react'
import {QueryErrorResetBoundary} from '@tanstack/react-query'
import {DataLoading} from '../../data-loading/data-loading'
import {ErrorFallback} from './error-fallback'
import type {SuspenseBoundaryProps} from './suspense-boundary.types'

import type {JSX} from 'react'

const DEFAULT_ERROR_MESSAGE = 'Oops, something went wrong'
const DEFAULT_RETRY_BUTTON_TEXT = 'Try again'

export function SuspenseBoundary({
    children,
    loadingFallback = <DataLoading />,
    boundaryKey,
    errorMessage = DEFAULT_ERROR_MESSAGE,
    errorFallback,
    retryButtonText = DEFAULT_RETRY_BUTTON_TEXT,
    showRetryButton = true,
}: SuspenseBoundaryProps): JSX.Element {
    return (
        <QueryErrorResetBoundary key={boundaryKey}>
            {({reset}) => (
                <ErrorBoundary
                    // When the error boundary resets (via resetError()), clear the React Query
                    // error cache so queries can retry. Flow: retry button → resetError() → onReset → reset()
                    onReset={reset}
                    fallback={(fallbackProps) => {
                        const resetErrorBoundary = () => {
                            fallbackProps.resetError()
                        }

                        if (errorFallback) {
                            return (
                                <>
                                    {errorFallback({
                                        error: fallbackProps.error as Error,
                                        resetErrorBoundary,
                                    })}
                                </>
                            )
                        }

                        return (
                            <ErrorFallback
                                errorMessage={errorMessage}
                                retryButtonText={retryButtonText}
                                showRetryButton={showRetryButton}
                                resetErrorBoundary={resetErrorBoundary}
                            />
                        )
                    }}
                >
                    <Suspense fallback={loadingFallback}>{children}</Suspense>
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    )
}
